import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { formatCurrency } from '../../utils/formatters'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material'
import { useBill, useUpdateBill } from '../../hooks/api/useBills'
import { useCustomers } from '../../hooks/api/useCustomers'
import { useSKUs } from '../../hooks/api/useSKUs'
import { useAreas } from '../../hooks/api/useAreas'
import { LoadingSpinner, StatusChip } from '../../components/ui'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

interface BillItem {
  sku_id: string
  sku_name: string
  packing: number
  quantity_ctn: number
  loose_pcs: number
  quantity_pcs: number
  price: number
  amount: number
  discount: number
}

const BillEditPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const advanceTaxEnabled = user?.company_features?.advance_tax !== false
  const loosePcsEnabled = user?.company_features?.loose_pcs === true
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const fromTable = searchParams.get('from') === 'table'
  const [formData, setFormData] = useState<any>({
    area: '',
    customer_id: '',
    sku_items: [],
    remarks: '',
  })
  const [billItems, setBillItems] = useState<BillItem[]>([])
  const [selectedSku, setSelectedSku] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [loosePieces, setLoosePieces] = useState(0)

  const { data: bill, isLoading, error } = useBill(id!)
  const { data: customersData } = useCustomers({ page: 1, limit: 100 })
  const { data: skusData } = useSKUs({ page: 1, limit: 100, active_only: true })
  const { data: areasData } = useAreas()
  const updateBillMutation = useUpdateBill()

  const customers = customersData?.results || []
  const skus = skusData?.results || []
  const areas = areasData?.results || []

  // Initialize form data when bill is loaded
  useEffect(() => {
    if (bill) {
      setFormData({
        area: bill.area_id._id,
        customer_id: bill.customer_id._id,
        sku_items: bill.sku_items.map(item => ({
          sku_id: typeof item.sku_id === 'string' ? item.sku_id : item.sku_id._id,
          quantity_ctn: item.full_quantity_ctn ?? item.quantity_ctn,
          loose_pcs: item.loose_pcs ?? 0
        })),
        remarks: bill.remarks,
      })

      // Convert sku_items to BillItem format for display
      const items: BillItem[] = bill.sku_items.map(item => {
        const skuId = typeof item.sku_id === 'string' ? item.sku_id : item.sku_id._id
        const skuName = typeof item.sku_id === 'string' ? 'Unknown SKU' : item.sku_id.name
        const existingPacking = typeof item.sku_id === 'string' ? 0 : item.sku_id.packing || 0
        const sku = skus.find(s => s._id === skuId)
        return {
          sku_id: skuId,
          sku_name: skuName,
          packing: sku?.packing || existingPacking,
          quantity_ctn: item.full_quantity_ctn ?? item.quantity_ctn,
          loose_pcs: item.loose_pcs ?? 0,
          quantity_pcs: item.quantity_pcs,
          price: item.price,
          amount: item.amount,
          discount: item.discount,
        }
      })
      setBillItems(items)
    }
  }, [bill, skus])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddItem = () => {
    if (!selectedSku || (quantity <= 0 && loosePieces <= 0)) return

    const sku = skus.find(s => s._id === selectedSku)
    if (!sku || !Number.isInteger(loosePieces) || loosePieces < 0 || loosePieces >= sku.packing) return

    const existingItem = billItems.find(item => item.sku_id === selectedSku)
    if (existingItem) {
      setBillItems(prev => prev.map(item =>
        item.sku_id === selectedSku
          ? { 
              ...item, 
              quantity_ctn: item.quantity_ctn + quantity + Math.floor((item.loose_pcs + loosePieces) / item.packing),
              loose_pcs: (item.loose_pcs + loosePieces) % item.packing,
              quantity_pcs: (item.quantity_ctn + quantity) * item.packing + item.loose_pcs + loosePieces,
              amount: item.price * (item.quantity_ctn + quantity + (item.loose_pcs + loosePieces) / item.packing)
            }
          : item
      ))
    } else {
      const newItem: BillItem = {
        sku_id: selectedSku,
        sku_name: sku.name,
        packing: sku.packing,
        quantity_ctn: quantity,
        loose_pcs: loosePieces,
        quantity_pcs: quantity * sku.packing + loosePieces,
        price: sku.standard_rate,
        amount: sku.standard_rate * (quantity + loosePieces / sku.packing),
        discount: 0,
      }
      setBillItems(prev => [...prev, newItem])
    }

    setSelectedSku('')
    setQuantity(1)
    setLoosePieces(0)
  }

  const handleRemoveItem = (skuId: string) => {
    setBillItems(prev => prev.filter(item => item.sku_id !== skuId))
  }

  const handleSubmit = () => {
    const { area, ...formDataWithoutArea } = formData
    const billData: any = {
      ...formDataWithoutArea,
      sku_items: billItems.map(item => ({
        sku_id: item.sku_id,
        quantity_ctn: item.quantity_ctn,
        loose_pcs: item.loose_pcs
      }))
    }

    updateBillMutation.mutate(
      { id: id!, data: billData },
      {
        onSuccess: () => {
          navigate(fromTable ? '/bills' : `/bills/${id}`)
        }
      }
    )
  }

  const calculateTotal = () => {
    return billItems.reduce((total, item) => {
      return total + item.amount // total = sum of all item amounts
    }, 0)
  }

  const calculateSummary = () => {
    // If we have original bill data and no modifications, use original summary
    if (bill && billItems.length === bill.sku_items.length && 
        billItems.every((item, index) => {
          const billSkuId = typeof bill.sku_items[index].sku_id === 'string' 
            ? bill.sku_items[index].sku_id 
            : bill.sku_items[index].sku_id._id
          return item.quantity_ctn === (bill.sku_items[index].full_quantity_ctn ?? bill.sku_items[index].quantity_ctn) &&
                 item.loose_pcs === (bill.sku_items[index].loose_pcs ?? 0) &&
                 item.sku_id === billSkuId
        })) {
      return bill.summary
    }
    
    // Otherwise calculate new summary
    const total = calculateTotal()
    const totalCtn = billItems.reduce((sum, item) => sum + item.quantity_ctn + item.loose_pcs / item.packing, 0)
    const totalPcs = billItems.reduce((sum, item) => sum + item.quantity_pcs, 0)
    
    // Get customer tax rate
    const customer = customers.find((c: any) => c._id === formData.customer_id)
    const advTaxRate = advanceTaxEnabled ? customer?.adv_tax || 0 : 0
    
    const advTax = (advTaxRate * total) / 100
    const netPayable = total + advTax
    
    return {
      quantity_ctn: totalCtn,
      quantity_pcs: totalPcs,
      total: total,
      adv_tax: advTax,
      net_payable: netPayable
    }
  }

  const selectedCustomer = customers.find((c: any) => c._id === formData.customer_id)
  const summary = calculateSummary()

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading bill details..." />
  }

  if (error || !bill) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          Bill not found or error loading bill details
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(fromTable ? '/bills' : `/bills/${id}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">Edit Bill - {bill.customer_name}</Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="body2" color="text.secondary">Bill #{bill._id} • Status:</Typography>
              <StatusChip label={bill.status} variant={bill.status === 'active' ? 'info' : bill.status === 'cancelled' ? 'danger' : 'neutral'} />
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={!formData.customer_id || billItems.length === 0 || updateBillMutation.isPending}
        >
          {updateBillMutation.isPending ? 'Updating...' : 'Update Bill'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Area</InputLabel>
                  <Select
                    value={formData.area}
                    onChange={(e) => handleInputChange('area', e.target.value)}
                    label="Area"
                    disabled
                  >
                    {areas.map((area) => (
                      <MenuItem key={area._id} value={area._id}>
                        {area.area_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={formData.customer_id}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    label="Customer"
                  >
                    {customers.map((customer: any) => (
                      <MenuItem key={customer._id} value={customer._id}>
                        {customer.name}{customer.customer_code ? ` - ${customer.customer_code}` : ''} - {customer.area_id?.area_name || 'Unknown Area'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Edit Items
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel>SKU</InputLabel>
                  <Select
                    value={selectedSku}
                    onChange={(e) => setSelectedSku(e.target.value)}
                    label="SKU"
                    disabled={skus.length === 0}
                  >
                    {skus.length === 0 ? (
                      <MenuItem disabled>Loading SKUs...</MenuItem>
                    ) : (
                      skus.map((sku) => (
                        <MenuItem key={sku._id} value={sku._id}>
                          {sku.name} - Packing: {sku.packing} - Price: Rs {sku.standard_rate}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={`Quantity (${uom.plural})`}
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
                  inputProps={{ min: 0, step: uom.allows_decimal ? 0.001 : 1 }}
                />
              </Grid>

              {loosePcsEnabled && <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Loose PCS"
                  type="number"
                  value={loosePieces}
                  onChange={(e) => setLoosePieces(Math.max(0, Math.floor(Number(e.target.value))))}
                  inputProps={{ min: 0, max: (skus.find((sku) => sku._id === selectedSku)?.packing || 1) - 1, step: 1 }}
                  error={Boolean(selectedSku && loosePieces >= (skus.find((sku) => sku._id === selectedSku)?.packing || 1))}
                />
              </Grid>}
              
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  disabled={!selectedSku || (quantity <= 0 && loosePieces <= 0) || loosePieces >= (skus.find((sku) => sku._id === selectedSku)?.packing || 1) || skus.length === 0}
                >
                  Add Item
                </Button>
              </Grid>
            </Grid>

            {billItems.length > 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU Name</TableCell>
                      <TableCell align="right">Packing</TableCell>
                      <TableCell align="right">{uom.plural}</TableCell>
                      {loosePcsEnabled && <TableCell align="right">Loose PCS</TableCell>}
                      <TableCell align="right">Pieces</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billItems.map((item) => (
                      <TableRow key={item.sku_id}>
                        <TableCell>{item.sku_name}</TableCell>
                        <TableCell align="right">{item.packing}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity_ctn}
                            inputProps={{ min: 0, step: uom.allows_decimal ? 0.001 : 1, style: { width: 55 } }}
                            onChange={(event) => {
                              const cartons = Number(event.target.value)
                              if (!Number.isFinite(cartons) || cartons < 0 || (!uom.allows_decimal && !Number.isInteger(cartons)) || (cartons === 0 && item.loose_pcs === 0)) return
                              setBillItems((prev) => prev.map((row) => row.sku_id === item.sku_id
                                ? { ...row, quantity_ctn: cartons, quantity_pcs: cartons * row.packing + row.loose_pcs, amount: row.price * (cartons + row.loose_pcs / row.packing) }
                                : row))
                            }}
                          />
                        </TableCell>
                        {loosePcsEnabled && <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.loose_pcs}
                            inputProps={{ min: 0, max: item.packing - 1, step: 1, style: { width: 55 } }}
                            onChange={(event) => {
                              const loose = Number(event.target.value)
                              if (!Number.isInteger(loose) || loose < 0 || loose >= item.packing || (loose === 0 && item.quantity_ctn === 0)) return
                              setBillItems((prev) => prev.map((row) => row.sku_id === item.sku_id
                                ? { ...row, loose_pcs: loose, quantity_pcs: row.quantity_ctn * row.packing + loose, amount: row.price * (row.quantity_ctn + loose / row.packing) }
                                : row))
                            }}
                          />
                        </TableCell>}
                        <TableCell align="right">{item.quantity_pcs}</TableCell>
                        <TableCell align="right">Rs {formatCurrency(item.price)}</TableCell>
                        <TableCell align="right">
                          Rs {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleRemoveItem(item.sku_id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {selectedCustomer && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body1">{selectedCustomer.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCustomer.area_id?.area_name || 'Unknown Area'}
                </Typography>
                {selectedCustomer.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomer.phone}
                  </Typography>
                )}
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total {uom.plural}:</Typography>
              <Typography variant="body2">{summary.quantity_ctn}</Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total Pieces:</Typography>
              <Typography variant="body2">{summary.quantity_pcs}</Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total:</Typography>
              <Typography variant="body2">Rs {summary.total.toFixed(2)}</Typography>
            </Box>
            
            {advanceTaxEnabled && <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Adv Tax ({bill?.adv_tax || selectedCustomer?.adv_tax || 0}%):</Typography>
              <Typography variant="body2">Rs {summary.adv_tax.toFixed(2)}</Typography>
            </Box>}
            
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h6">Net Payable:</Typography>
              <Typography variant="h6">Rs {summary.net_payable.toFixed(2)}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default BillEditPage
